/**
 * Global request cancellation service
 * Tracks all active HTTP requests and provides ability to cancel them
 * Excludes authentication-related requests to ensure MSAL redirect auth works
 */
class RequestCancellationService {
  constructor() {
    // Map of request IDs to AbortControllers
    this.activeRequests = new Map()
    // Counter for unique request IDs
    this.requestIdCounter = 0
    // Routes/endpoints that should never be cancelled (auth-related)
    this.protectedRoutes = [
      '/auth/',
      '/token',
      '/refresh',
      '/login',
      '/msal',
      '/landing',
      '/impersonate',
    ]
  }

  /**
   * Check if a request URL should be protected from cancellation
   * @param {string} url - Request URL
   * @returns {boolean}
   */
  isProtectedRoute(url) {
    if (!url) return false
    return this.protectedRoutes.some((route) =>
      url.toLowerCase().includes(route.toLowerCase())
    )
  }

  /**
   * Register a new request with cancellation support
   * @param {Object} config - Axios request config
   * @returns {Object} - Modified config with AbortController
   */
  registerRequest(config) {
    // Don't add cancellation to protected routes (auth-related)
    if (this.isProtectedRoute(config.url)) {
      return config
    }

    const requestId = ++this.requestIdCounter

    // If request already has a signal, track it but don't override
    if (config.signal) {
      // Track requests with existing signals so we know about them
      // Note: We can't cancel these directly, but the module that created
      // the signal should handle cancellation
      this.activeRequests.set(requestId, {
        controller: null, // Can't access controller from signal
        url: config.url,
        method: config.method,
        hasExternalSignal: true, // Flag to indicate external signal
        signal: config.signal, // Store reference to signal
      })

      // Store requestId on config for cleanup
      config._requestCancellationId = requestId
      return config
    }

    // Create AbortController for this request
    const abortController = new AbortController()

    // Store the controller
    this.activeRequests.set(requestId, {
      controller: abortController,
      url: config.url,
      method: config.method,
      hasExternalSignal: false,
    })

    // Add signal to config
    config.signal = abortController.signal

    // Store requestId on config for cleanup
    config._requestCancellationId = requestId

    return config
  }

  /**
   * Unregister a request (called when request completes)
   * @param {Object} config - Axios request config
   */
  unregisterRequest(config) {
    if (config._requestCancellationId) {
      this.activeRequests.delete(config._requestCancellationId)
    }
  }

  /**
   * Cancel a specific request by ID
   * @param {number} requestId
   */
  cancelRequest(requestId) {
    const request = this.activeRequests.get(requestId)
    if (request) {
      request.controller.abort()
      this.activeRequests.delete(requestId)
    }
  }

  /**
   * Cancel all active requests (except protected routes)
   * @param {boolean} force - If true, cancels even protected routes (use with caution)
   */
  cancelAllRequests(force = false) {
    const requestsToCancel = []
    const requestsWithExternalSignals = []

    this.activeRequests.forEach((request, requestId) => {
      // Skip protected routes unless forced
      if (!force && this.isProtectedRoute(request.url)) {
        return
      }

      // Track requests with external signals separately
      // (they need to be cancelled by their respective modules)
      if (request.hasExternalSignal) {
        requestsWithExternalSignals.push({ requestId, request })
      } else {
        requestsToCancel.push({ requestId, request })
      }
    })

    // Cancel all non-protected requests that we control
    requestsToCancel.forEach(({ requestId, request }) => {
      if (request.controller) {
        request.controller.abort()
      }
      this.activeRequests.delete(requestId)
    })

    // Return count of cancelled requests and info about external signals
    return {
      cancelled: requestsToCancel.length,
      externalSignals: requestsWithExternalSignals.length,
      total: requestsToCancel.length + requestsWithExternalSignals.length,
    }
  }

  /**
   * Get count of active requests
   * @returns {number}
   */
  getActiveRequestCount() {
    return this.activeRequests.size
  }

  /**
   * Get count of active requests (excluding protected)
   * @returns {number}
   */
  getCancellableRequestCount() {
    let count = 0
    this.activeRequests.forEach((request) => {
      if (!this.isProtectedRoute(request.url)) {
        count++
      }
    })
    return count
  }
}

// Export singleton instance
export default new RequestCancellationService()
